import React from 'react';
import { AreaChart } from '@tremor/react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { isNumber } from 'lodash';
import Link from '@dashboard/components/Link';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import ChartMeta from './ChartMeta';

export function StatMetric({ label, tooltip, value, isLoading = false }) {
  return (
    <div className="leading-tight font-semibold text-gray-900 dark:text-foreground pt-[4px] text-sm">
      <div className="text-sm font-normal">
        {label}
        {tooltip ? <> {tooltip}</> : null}
      </div>
      {isLoading ? (
        <Skeleton className="mt-1.5 h-7 w-20" />
      ) : value !== undefined && value !== null ? (
        <div className="text-xl mt-1.5 font-normal">{value}</div>
      ) : null}
    </div>
  );
}

export function StatTooltip({ children }) {
  return <InfoTooltip>{children}</InfoTooltip>;
}

export function StatHeader({ action, children }) {
  return (
    <div className="flex flex-row flex-wrap justify-between items-start mb-2">
      {children}
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function ViewAllLink({ href }) {
  return (
    <Link href={href} className="flex items-center gap-x-1 whitespace-nowrap text-sm">
      View all <ArrowRightIcon className="w-4 h-4" />
    </Link>
  );
}

export function StatBody({ children }) {
  return <div className="py-3">{children}</div>;
}

export function StatChart({
  isLoading,
  data,
  categories,
  maxValue,
  minValue,
  valueFormatter,
  heightClass = 'h-44',
}) {
  if (isLoading) {
    return <Skeleton className={`mt-4 w-full ${heightClass}`} />;
  }

  return (
    <div className="relative pl-8 [&_.stroke-gray-500]:stroke-gray-400 [&_.text-gray-500]:text-gray-400 dark:[&_.text-gray-500]:text-muted-foreground">
      {data && <ChartMeta maxValue={maxValue} minValue={minValue} />}
      <AreaChart
        className={`mt-4 ${heightClass}`}
        data={data}
        categories={categories}
        index="timestamp"
        colors={['gray']}
        valueFormatter={valueFormatter ? v => (isNumber(v) ? valueFormatter(v) : v) : undefined}
        showAnimation
        allowDecimals={false}
        showLegend={false}
        showGridLines={false}
        showYAxis={false}
        showXAxis
        startEndOnly
        showGradient
        padding={{ left: 16, right: 16 }}
      />
    </div>
  );
}
